import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Service } from '../../services/api/service-map.api';

interface ServiceNodeData extends Service {
  connectionsCount: number;
  incomingCount: number;
  outgoingCount: number;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  onClick?: (service: Service) => void;
  throughput?: number;
  errorRate?: number;
  avgLatency?: number;
  p99Latency?: number;
  requestsPerSec?: number;
  trend?: 'up' | 'down' | 'stable';
  healthScore?: number;
}

const typeIcons: Record<string, JSX.Element> = {
  api: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  service: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  ),
  database: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  ),
  cache: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  queue: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  web: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  external: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  ),
};

const statusConfig = {
  healthy: {
    gradient: 'from-emerald-500/20 to-emerald-600/5',
    border: 'border-emerald-500/50',
    glow: 'shadow-emerald-500/20',
    dot: 'bg-emerald-500',
    pulse: 'animate-pulse-slow',
    icon: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  warning: {
    gradient: 'from-amber-500/20 to-amber-600/5',
    border: 'border-amber-500/50',
    glow: 'shadow-amber-500/20',
    dot: 'bg-amber-500',
    pulse: 'animate-pulse',
    icon: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-500/10',
  },
  critical: {
    gradient: 'from-red-500/20 to-red-600/5',
    border: 'border-red-500/50',
    glow: 'shadow-red-500/20',
    dot: 'bg-red-500',
    pulse: 'animate-pulse-fast',
    icon: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-500/10',
  },
  unknown: {
    gradient: 'from-slate-500/20 to-slate-600/5',
    border: 'border-slate-500/50',
    glow: 'shadow-slate-500/20',
    dot: 'bg-slate-500',
    pulse: '',
    icon: 'text-slate-600 dark:text-slate-400',
    iconBg: 'bg-slate-500/10',
  },
};

function ServiceNode({ data }: NodeProps<ServiceNodeData>) {
  const [isHovered, setIsHovered] = useState(false);
  const status = statusConfig[data.status] || statusConfig.unknown;
  const icon = typeIcons[data.type?.toLowerCase()] || typeIcons.api;

  const handleClick = () => {
    if (data.onClick) {
      data.onClick(data);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const errorRate = data.errorRate ?? (data.metadata?.error_rate || 0);
  const avgLatency = data.avgLatency ?? (data.metadata?.avg_latency_ms || 0);
  const requestsPerSec = data.requestsPerSec ?? (data.metadata?.calls_per_minute ? data.metadata.calls_per_minute / 60 : 0);

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative group cursor-pointer transition-all duration-300 ease-out
        ${data.isSelected ? 'scale-105 z-50' : 'scale-100'}
        ${data.isDimmed ? 'opacity-40' : 'opacity-100'}
        ${data.isHighlighted ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-gray-900' : ''}
      `}
    >
      {/* Glow effect */}
      <div className={`
        absolute -inset-1 rounded-2xl blur-xl transition-opacity duration-300
        bg-gradient-to-br ${status.gradient}
        ${isHovered || data.isSelected ? 'opacity-100' : 'opacity-0'}
      `} />

      {/* Main card */}
      <div className={`
        relative rounded-xl border backdrop-blur-sm
        bg-gradient-to-br ${status.gradient}
        ${status.border}
        ${isHovered ? `shadow-xl ${status.glow}` : 'shadow-lg'}
        dark:bg-gray-900/80 bg-white/90
        min-w-[220px] overflow-hidden
        transition-all duration-300
      `}>
        {/* Top accent bar */}
        <div className={`absolute top-0 left-0 right-0 h-1 ${status.dot}`} />

        {/* Handles */}
        <Handle
          type="target"
          position={Position.Top}
          className={`
            !w-3 !h-3 !border-2 !border-white dark:!border-gray-800
            ${status.dot} !-top-1.5
          `}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          className={`
            !w-3 !h-3 !border-2 !border-white dark:!border-gray-800
            ${status.dot} !-bottom-1.5
          `}
        />

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            {/* Icon container */}
            <div className={`
              flex-shrink-0 w-10 h-10 rounded-lg
              flex items-center justify-center
              ${status.iconBg} ${status.icon}
              transition-transform duration-300
              ${isHovered ? 'scale-110' : 'scale-100'}
            `}>
              {icon}
            </div>

            {/* Title and type */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {data.name}
                </h3>
                {/* Status dot */}
                <span className={`flex-shrink-0 w-2 h-2 rounded-full ${status.dot} ${status.pulse}`} />
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {data.type}
                </span>
                {data.language && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {data.language}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            {/* Requests/sec */}
            <div className="text-center p-2 rounded-lg bg-gray-100/50 dark:bg-gray-800/50">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">req/s</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatNumber(Math.round(requestsPerSec))}
              </div>
            </div>

            {/* Latency */}
            <div className="text-center p-2 rounded-lg bg-gray-100/50 dark:bg-gray-800/50">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">latency</div>
              <div className={`text-sm font-semibold ${
                avgLatency > 500 ? 'text-red-600 dark:text-red-400' :
                avgLatency > 200 ? 'text-amber-600 dark:text-amber-400' :
                'text-gray-900 dark:text-white'
              }`}>
                {avgLatency < 1 ? '<1' : Math.round(avgLatency)}ms
              </div>
            </div>

            {/* Error rate */}
            <div className="text-center p-2 rounded-lg bg-gray-100/50 dark:bg-gray-800/50">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">errors</div>
              <div className={`text-sm font-semibold ${
                errorRate > 5 ? 'text-red-600 dark:text-red-400' :
                errorRate > 1 ? 'text-amber-600 dark:text-amber-400' :
                'text-emerald-600 dark:text-emerald-400'
              }`}>
                {errorRate.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Connection indicators */}
          <div className="mt-3 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span>{data.incomingCount || 0} in</span>
            </div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <span>{data.outgoingCount || 0} out</span>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </div>
          </div>

          {/* Environment badge */}
          {data.environment && (
            <div className="mt-3 flex items-center justify-center">
              <span className={`
                inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                ${data.environment === 'production'
                  ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : data.environment === 'staging'
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                }
              `}>
                {data.environment}
              </span>
            </div>
          )}
        </div>

        {/* Click indicator */}
        <div className={`
          absolute bottom-2 right-2 opacity-0 transition-opacity duration-200
          ${isHovered ? 'opacity-100' : ''}
        `}>
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default memo(ServiceNode);
